import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import {
  selectAllMsaJobs,
  selectAllLandCoverChangeData,
  setLandCoverChangeCalculating,
  setLandCoverChange,
  setLandCoverChangeFailed,
} from "@app/features/msa/msaSlice";
import { calculateLandCoverChange } from "@pages/project/results/components/msa/landCoverChangeUtils";

/**
 * App-level hook that watches all MSA jobs across all projects.
 * When both baseline and project calculations are completed, it triggers
 * the Land Cover Change calculation — but only if the job IDs have changed
 * (i.e. new results arrived). This hook must be mounted once in App.tsx so
 * it survives page navigation and never loses its in-flight tracking.
 */
export function useLandCoverChangeAutoTrigger() {
  const dispatch = useAppDispatch();
  const allMsaJobs = useAppSelector(selectAllMsaJobs);
  const allLccData = useAppSelector(selectAllLandCoverChangeData);

  // Track in-flight cache keys so we never start the same calculation twice.
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    for (const [projectId, projectJobs] of Object.entries(allMsaJobs)) {
      const baseline = projectJobs?.baseline;
      const project = projectJobs?.project;

      if (
        baseline?.status !== "completed" ||
        !baseline.result?.zip_url ||
        !baseline.jobId ||
        project?.status !== "completed" ||
        !project.result?.zip_url ||
        !project.jobId
      ) {
        continue;
      }

      const baselineJobId = baseline.jobId;
      const projectJobId = project.jobId;
      const cacheKey = `${projectId}|${baselineJobId}|${projectJobId}`;

      const existingResult = allLccData[projectId]?.landCoverChange;

      // Cache hit: same job IDs already computed
      if (
        existingResult?.baselineJobId === baselineJobId &&
        existingResult?.projectJobId === projectJobId
      ) {
        continue;
      }

      // Already running this combination
      if (inFlightRef.current.has(cacheKey)) {
        continue;
      }

      inFlightRef.current.add(cacheKey);
      dispatch(setLandCoverChangeCalculating({ projectId }));

      const baselineZipUrl = baseline.result.zip_url;
      const projectZipUrl = project.result.zip_url;

      calculateLandCoverChange(baselineZipUrl, projectZipUrl)
        .then((result) => {
          dispatch(
            setLandCoverChange({
              projectId,
              result: { ...result, baselineJobId, projectJobId },
            })
          );
        })
        .catch((err: unknown) => {
          const message =
            err instanceof Error
              ? err.message
              : "Unknown error during LCC calculation";
          dispatch(setLandCoverChangeFailed({ projectId, error: message }));
        })
        .finally(() => {
          inFlightRef.current.delete(cacheKey);
        });
    }
  }, [allMsaJobs, allLccData, dispatch]);
}
